/*
  Back-in-stock sign-up.
  ----------------------
  Runs snippets/product-stock-notice.liquid. Talks to Klaviyo's *client* API, which is
  the half of the API a public key is allowed to reach — no secret key is ever in the
  theme, and there is no server hop.

  Two calls, in this order:
    POST /client/back-in-stock-subscriptions   always. The subscription itself.
    POST /client/subscriptions                 only with a list id, only when the customer
                                               ticked the marketing opt-in, and only when
                                               Shopify's customerPrivacy.marketingAllowed()
                                               agrees.

  The second is a marketing consent and the first is not, so a failure there is logged
  and swallowed: the customer asked to be told when the size is back, and they will be.

  Markup contract:
    [data-bis-form]        the form. Carries the company id, the list id, the catalog
                           prefix and every string this file can print.
    [data-bis-email]       the email field
    [data-bis-opt-in]      the marketing checkbox, when the section shows one
    [data-bis-submit]      the submit button
    [data-bis-status]      aria-live region for the sending / done / failed line

  Which variant is being asked about is read from the panel's own `[name="id"]` at
  submit time — the same control the gallery and Shopify's instalments banner read, so
  nothing here needs telling when the customer changes size.
*/
(function () {
  if (window.__klaviyoBis) return;
  window.__klaviyoBis = true;

  var REVISION = '2026-07-15';
  var BIS_URL = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/';
  var LIST_URL = 'https://a.klaviyo.com/client/subscriptions/';

  function setStatus(form, message, tone) {
    var status = form.querySelector('[data-bis-status]');
    if (!status) return;
    status.textContent = message || '';
    status.classList.toggle('is-error', tone === 'error');
    status.classList.toggle('is-success', tone === 'success');
  }

  function variantId(form) {
    var root = form.closest('[data-product-form]');
    var select = root && root.querySelector('[data-pf-variant-select]');
    return select && select.value ? select.value : '';
  }

  function post(url, companyId, body) {
    return fetch(url + '?company_id=' + encodeURIComponent(companyId), {
      method: 'POST',
      headers: { 'content-type': 'application/json', revision: REVISION },
      body: JSON.stringify(body),
    }).then(function (response) {
      // 202 Accepted with an empty body is the success case for both endpoints.
      if (!response.ok) throw new Error(String(response.status));
      return response;
    });
  }

  function subscribe(form) {
    var companyId = form.getAttribute('data-company-id');
    var email = form.querySelector('[data-bis-email]');
    var value = email ? email.value.trim() : '';
    if (!companyId || !value) return;

    var id = variantId(form);
    if (!id) {
      setStatus(form, form.getAttribute('data-error') || '', 'error');
      return;
    }

    var submit = form.querySelector('[data-bis-submit]');
    if (submit) {
      submit.disabled = true;
      submit.classList.add('is-loading');
    }
    setStatus(form, form.getAttribute('data-sending') || '');

    post(BIS_URL, companyId, {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          channels: ['EMAIL'],
          profile: { data: { type: 'profile', attributes: { email: value } } },
        },
        relationships: {
          variant: {
            data: {
              type: 'catalog-variant',
              // Klaviyo's Shopify catalog keys a variant as
              // `$shopify:::$default:::<variant id>`; the prefix is printed by the
              // snippet so an integration on another catalog is one attribute away.
              id: (form.getAttribute('data-catalog-prefix') || '') + id,
            },
          },
        },
      },
    })
      .then(function () {
        joinList(form, companyId, value);

        form.classList.add('is-done');
        setStatus(form, form.getAttribute('data-success') || '', 'success');
      })
      .catch(function () {
        setStatus(form, form.getAttribute('data-error') || '', 'error');
      })
      .then(function () {
        if (!submit) return;
        submit.disabled = false;
        submit.classList.remove('is-loading');
      });
  }

  // Shopify's store-wide marketing consent. Absent means the Customer Privacy API never
  // loaded, and the ticked box is then the only consent signal there is — so it stands.
  function marketingAllowed() {
    var api = window.Shopify && window.Shopify.customerPrivacy;
    if (!api || typeof api.marketingAllowed !== 'function') return true;
    try {
      return api.marketingAllowed();
    } catch (e) {
      return true;
    }
  }

  function joinList(form, companyId, email) {
    var listId = form.getAttribute('data-list-id');
    if (!listId) return;

    // No checkbox rendered means the section decided the sign-up is the opt-in. One
    // rendered and left unticked is a refusal, and is honoured.
    var optIn = form.querySelector('[data-bis-opt-in]');
    if (optIn && !optIn.checked) return;

    // Declining marketing in the cookie banner outranks the tick: the list join is
    // marketing, while the back-in-stock alert above is the service they asked for.
    if (!marketingAllowed()) return;

    post(LIST_URL, companyId, {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'Back in stock: ' + (form.getAttribute('data-source') || ''),
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: email,
                subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } },
              },
            },
          },
        },
        relationships: { list: { data: { type: 'list', id: listId } } },
      },
    }).catch(function () {
      /* The subscription that matters already landed. */
    });
  }

  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || !form.matches || !form.matches('[data-bis-form]')) return;
    e.preventDefault();
    subscribe(form);
  });

  /* A different size is a different question, so the answer to the last one goes —
     including the answered state, which would otherwise hide the field for a variant
     nobody has signed up to. */
  document.addEventListener('change', function (e) {
    var target = e.target;
    if (!target || !target.matches || !target.matches('[name="id"]')) return;

    var root = target.closest('[data-product-form]') || document;
    var forms = root.querySelectorAll('[data-bis-form]');
    for (var i = 0; i < forms.length; i++) {
      forms[i].classList.remove('is-done');
      setStatus(forms[i], '');
    }
  });
})();
